import {
  getDocument,
  GlobalWorkerOptions,
  version as pdfjsVersion,
} from "pdfjs-dist";
import {
  convertPdfBufferToSvgPages,
  initializePdfToSvgRuntime,
} from "./pdfToSvgRuntime";

GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;

export const PDF_PAGE_IMAGE_OPTIONS = {
  targetWidth: 2400,
  quality: 0.94,
  outputFormat: "svg-first",
};

function canvasToJpegFile(canvas, fileName, quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to render PDF page: ${fileName}`));
          return;
        }

        resolve(
          new File([blob], fileName, {
            type: "image/jpeg",
            lastModified: Date.now(),
          })
        );
      },
      "image/jpeg",
      quality
    );
  });
}

function createSvgFile(svgMarkup, fileName) {
  return new File([svgMarkup], fileName, {
    type: "image/svg+xml",
    lastModified: Date.now(),
  });
}

async function renderPdfPagesToJpegFiles(file, options = {}) {
  const {
    targetWidth = PDF_PAGE_IMAGE_OPTIONS.targetWidth,
    quality = PDF_PAGE_IMAGE_OPTIONS.quality,
  } = options;
  const buffer = await file.arrayBuffer();
  const loadingTask = getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const baseName = String(file.name || "document").replace(/\.[^.]+$/, "");
  const pageFiles = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error(`Failed to render PDF page ${pageNumber}`);
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    pageFiles.push(
      await canvasToJpegFile(canvas, `${baseName}-page-${pageNumber}.jpg`, quality)
    );
  }

  return pageFiles;
}

async function renderPdfPagesToSvgFiles(file) {
  await initializePdfToSvgRuntime();
  const baseName = String(file.name || "document").replace(/\.[^.]+$/, "");
  const buffer = await file.arrayBuffer();
  const svgPages = await convertPdfBufferToSvgPages(buffer);

  return svgPages.map((svgMarkup, index) =>
    createSvgFile(svgMarkup, `${baseName}-page-${index + 1}.svg`)
  );
}

export async function renderPdfPagesToImageFiles(
  file,
  options = {}
) {
  const { outputFormat = PDF_PAGE_IMAGE_OPTIONS.outputFormat } = options;

  if (outputFormat === "jpeg") {
    return renderPdfPagesToJpegFiles(file, options);
  }

  try {
    if (outputFormat === "svg-first" || outputFormat === "svg") {
      const svgFiles = await renderPdfPagesToSvgFiles(file);
      if (svgFiles.length > 0) {
        return svgFiles;
      }
    }
  } catch (error) {
    if (outputFormat === "svg") {
      throw error;
    }
    console.warn("PDF to SVG conversion failed; falling back to raster preview.", error);
  }

  return renderPdfPagesToJpegFiles(file, options);
}
